import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { CheckmarkIcon } from '@inter-ikea-kompis/icons';
import type { ITheme } from '@inter-ikea-kompis/types';
import { IdGenerator } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';

import CheckboxAlignButtonEnum from '../../enums/CheckboxAlignButtonEnum.js';
import CheckboxLayoutEnum from '../../enums/CheckboxLayoutEnum.js';
import CheckboxStyle from './CheckboxStyle.js';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 
 * @event change When the checkbox is checked or unchecked
 */

export default class Checkbox extends KompisElement {
	// A text that describes the action of the checkbox.
	@property({ type: String })
	public label: string | null = null;

	// Like a native input element.
	@property({ type: Boolean })
	public required = false;

	// Like a native input element.
	@property({ type: Boolean })
	public disabled = false;

	// Applicable when type is switch or checkbox.
	@property({ type: Boolean })
	public checked = false;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Error message.
	@property({ type: String })
	public errorMessage: string | null = null;

	// Is input invalid.
	@property({ type: Boolean })
	public invalid = false;

	// Where the label should be.
	@property({ type: String })
	public alignButton: CheckboxAlignButtonEnum = CheckboxAlignButtonEnum.left;

	// Changes the style of the checkbox.
	@property({ type: String })
	public layout: CheckboxLayoutEnum = CheckboxLayoutEnum.emphasised;

	// Enables the hovered styling.
	@property({ type: Boolean })
	public hovered = false;

	private showOutline = false; // State to show outline
	private pointerIsDown = false; // To distinguish between how focus have been captured
	private uniqueId = new IdGenerator().generateId();
	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const direction = window.getComputedStyle(this).direction;

		return html`
			<style>
				${CheckboxStyle.getStyle({
					isDisabled: this.disabled,
					showOutline: this.showOutline,
					isChecked: this.checked,
					invalid: this.invalid,
					theme: this.theme,
					alignButton: this.alignButton,
					layout: this.layout,
					hover: this.hovered,
					direction: direction
				})}
			</style>
			${this.getCheckbox()}
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-icon', Icon);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}
	/**
	 * @returns Template.
	 */
	private getCheckbox(): RenderResult {
		return html`
			<div class="container">
				<div class="checkbox">
					<div class="outline"></div>
					<kompis-icon .theme="${this.theme}" .icon="${CheckmarkIcon}"></kompis-icon>
				</div>
				${this.getCheckableInputElement()} ${this.getLabel()}
			</div>
			${this.getErrorMessage()}
		`;
	}

	/**
	 * @returns Template.
	 */
	private getCheckableInputElement(): RenderResult {
		return html`
			<input
				type="checkbox"
				?checked="${this.checked}"
				?disabled="${this.disabled}"
				id="${this.uniqueId}"
				@touchdown="${this.onPointerDown.bind(this)}"
				@pointerdown="${this.onPointerDown.bind(this)}"
				@focusout="${this.onFocusOut.bind(this)}"
				@focus="${this.onFocusIn.bind(this)}"
				@change="${this.onChange.bind(this)}"
				>
			</input>
    	`;
	}

	/**
	 * @returns Template.
	 */
	private getErrorMessage(): RenderResult {
		if (!this.invalid) {
			return null;
		}

		return html`
			<span class="errorMessage">
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodyMedium}"
					>${this.errorMessage}</kompis-text
				>
			</span>
		`;
	}

	/**
	 * HTML event handler.
	 */
	private onFocusOut(): void {
		this.showOutline = false;
		this.pointerIsDown = false;
		this.update();
	}

	/**
	 * HTML event handler.
	 */
	private onFocusIn(): void {
		this.showOutline = !this.pointerIsDown;
		this.update();
	}

	/**
	 * HTML event handler.
	 *
	 * @param event Pointer event.
	 */
	private onPointerDown(event: Event): void {
		event.preventDefault();
		this.pointerIsDown = true;
		this.showOutline = false;
	}

	/**
	 * Runs when input event occurs.
	 *
	 * @param event HTMLEvent.
	 */
	private onChange(event: InputEvent): void {
		event.stopPropagation();
		event.preventDefault();

		this.dispatchEvent(
			new CustomEvent('change', {
				bubbles: true,
				detail: {
					checked: (<HTMLInputElement>event.target).checked
				}
			})
		);
	}

	/**
	 * @returns Template.
	 */
	private getLabel(): RenderResult {
		if (!this.label) {
			return null;
		}

		return html`
			<div class="labelDivider"></div>
			<label
				for="${this.uniqueId}"
				@touchdown="${this.onPointerDown.bind(this)}"
				@pointerdown="${this.onPointerDown.bind(this)}"
			>
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodyMedium}">
					${this.label}
				</kompis-text>
			</label>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-checkbox': Checkbox;
	}
}
